import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Sparkles, ArrowLeft, LogIn, UserPlus, Search, ShoppingCart, Calculator, Globe, Users, ScanSearch, Clapperboard, FileText, Bell } from "lucide-react";

const IMG_SHOWROOM = "https://customer-assets-m6fa6gv7.emergentagent.net/job_imdad-tiles/artifacts/4vsdrhv0_%D8%B5%D9%88%D8%B1%D8%A9%20ChatGPT%202%20%D8%A3%D9%83%D8%AA%D9%88%D8%A8%D8%B1%202026%D8%8C%2002_42_37%20%D8%B5-2.png";
const IMG_ANALYTICS = "https://customer-assets-m6fa6gv7.emergentagent.net/job_imdad-tiles/artifacts/c91a8nex_%D8%B5%D9%88%D8%B1%D8%A9%20ChatGPT%202%20%D8%A3%D9%83%D8%AA%D9%88%D8%A8%D8%B1%202026%D8%8C%2002_42_38%20%D8%B5-3.png";
const IMG_CORPORATE = "https://customer-assets-m6fa6gv7.emergentagent.net/job_imdad-tiles/artifacts/8c5gxhq7_%D8%B5%D9%88%D8%B1%D8%A9%20ChatGPT%202%20%D8%A3%D9%83%D8%AA%D9%88%D8%A8%D8%B1%202026%D8%8C%2002_42_40%20%D8%B5-4.png";

const FEATURE_GROUPS = [
  { icon: ShoppingCart, title: "البيع وإدارة المنتجات", testid: "feature-sales",
    desc: "بيع وشراء بين التجار، مبيعات إلكترونية وحضورية داخل المحل، إدارة المنتجات والكميات، ومتجر عام لعرض بضاعتك." },
  { icon: Calculator, title: "المحاسبة ومتابعة الأعمال", testid: "feature-accounting",
    desc: "لوحة محاسبية تجمع المبيعات والمشتريات، متابعة الديون والدفعات والمرتجعات، وتحليلات واضحة لأداء عملك." },
  { icon: Globe, title: "التوريد الخارجي", testid: "feature-sourcing",
    desc: "وكيل ذكي يبحث عن المورّدين خارج سوريا، يراسلهم بلغتهم من بريدك، ويتابع ردودهم نيابةً عنك." },
  { icon: Users, title: "إدارة الموظفين", testid: "feature-employees",
    desc: "حسابات لموظفيك بصلاحيات محددة، تنفيذ البيع والشراء باسم المتجر، وطلبات الإجازات والموافقة عليها." },
  { icon: ScanSearch, title: "البحث بالصور", testid: "feature-ai",
    desc: "صوّر المنتج أو ارفع صورته، ودع الذكاء الاصطناعي يجد المطابق له لدى التجار الآخرين خلال ثوانٍ." },
  { icon: Clapperboard, title: "التسويق وصناعة الفيديوهات", testid: "feature-marketing",
    desc: "اختر منتجاتك وأنشئ فيديوهات تسويقية قصيرة بالذكاء الاصطناعي مع تعليق صوتي عربي، جاهزة للمشاركة." },
  { icon: FileText, title: "الفواتير وإدارة المخزون", testid: "feature-invoices",
    desc: "فواتير PDF لمبيعاتك، قراءة ذكية لفواتيرك القديمة وتحويلها إلى بيانات، تصدير Excel، وتنبيهات نقص المخزون." },
  { icon: Bell, title: "الإشعارات والتواصل", testid: "feature-chat",
    desc: "دردشة مباشرة مع التجار، إشعارات فورية داخل المنصة وعبر البريد وواتساب، وتنبيهات السوق في محافظتك." },
];

const useCounter = (target, duration = 1500) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target || target <= 0) { setVal(0); return; }
    let start = null;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

const Counter = ({ target, label, suffix = "+" }) => {
  const v = useCounter(target);
  return (
    <div className="text-center" data-testid={`counter-${label}`}>
      <div className="text-5xl md:text-6xl font-extrabold text-[#09637E] tracking-tight">
        {v.toLocaleString("en-US")}<span className="text-[#088395]">{suffix}</span>
      </div>
      <div className="mt-2 text-[#666666] font-bold">{label}</div>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, desc, testid }) => (
  <div className="group bg-white border border-[#7AB2B2] rounded-2xl p-6 hover:border-[#09637E] hover:-translate-y-1 hover:shadow-xl transition-all duration-300" data-testid={testid}>
    <div className="w-14 h-14 rounded-xl bg-[#EBF4F6] group-hover:bg-[#09637E] text-[#09637E] group-hover:text-white flex items-center justify-center mb-4 transition-colors">
      <Icon size={26} />
    </div>
    <h3 className="text-xl font-extrabold text-[#333333] mb-2">{title}</h3>
    <p className="text-[#666666] leading-relaxed text-sm">{desc}</p>
  </div>
);

const Landing = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ merchants: 0, photos: 0, reservations: 0 });

  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const ctaTo = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/register";
  const ctaLabel = user ? "اذهب إلى لوحتك" : "سجل الآن مجاناً";

  return (
    <div className="min-h-screen bg-[#EBF4F6]" dir="rtl" data-testid="landing-page">
      {/* Top nav */}
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur border-b border-[#7AB2B2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <span className="font-extrabold text-[#09637E] text-xl">إمداد</span>
          </Link>
          <nav className="flex items-center gap-2">
            {user ? (
              <Link to={ctaTo} data-testid="landing-go-dashboard"
                className="h-10 px-4 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center gap-1">
                لوحتي <ArrowLeft size={16} />
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="landing-login"
                  className="h-10 px-4 rounded-lg border-2 border-[#09637E] text-[#09637E] hover:bg-[#EBF4F6] font-bold text-sm flex items-center gap-1">
                  <LogIn size={16} /> دخول
                </Link>
                <Link to="/register" data-testid="landing-register"
                  className="h-10 px-4 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center gap-1">
                  <UserPlus size={16} /> سجل
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#7AB2B2] text-xs font-bold text-[#09637E] mb-5">
              <Sparkles size={14} /> فريقك الكامل في مكان واحد
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-[#333333] leading-snug md:leading-[1.2] mb-5" data-testid="hero-headline">
              <span className="text-[#09637E]">إمداد</span> — المنصة المتكاملة الأولى في سوريا، لخدمة التجار
            </h1>
            <p className="text-lg md:text-xl text-[#666666] leading-relaxed mb-8" data-testid="hero-subheadline">
              بِع، ورِّد، حاسِب، وسوِّق — وأنشئ فيديوهاتك، كل ذلك في مكان واحد: إمداد.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={ctaTo} data-testid="hero-cta"
                className="h-14 px-8 rounded-xl bg-[#09637E] hover:bg-[#088395] text-white font-extrabold text-lg flex items-center gap-2 shadow-lg shadow-[#09637E]/20 transition-all hover:-translate-y-0.5">
                {ctaLabel} <ArrowLeft size={20} />
              </Link>
              <a href="#features" className="h-14 px-8 rounded-xl border-2 border-[#09637E] text-[#09637E] hover:bg-white font-bold text-lg flex items-center gap-2" data-testid="hero-explore">
                <Search size={20} /> اكتشف المميزات
              </a>
            </div>
          </div>

          {/* Business collage */}
          <div className="relative h-[420px] md:h-[520px]">
            <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-3">
              <div className="rounded-2xl overflow-hidden border-4 border-white shadow-xl row-span-2">
                <img src={IMG_SHOWROOM} alt="صالة عرض مواد بناء فاخرة" className="w-full h-full object-cover" data-testid="hero-img-showroom" />
              </div>
              <div className="rounded-2xl overflow-hidden border-4 border-white shadow-xl">
                <img src={IMG_ANALYTICS} alt="تحليلات ومؤشرات الأعمال" className="w-full h-full object-cover" data-testid="hero-img-analytics" />
              </div>
              <div className="rounded-2xl overflow-hidden border-4 border-white shadow-xl">
                <img src={IMG_CORPORATE} alt="مقر شركة حديث" className="w-full h-full object-cover" data-testid="hero-img-corporate" />
              </div>
            </div>
            {/* Decorative blur blob */}
            <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-[#7AB2B2]/40 blur-3xl -z-10" />
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#088395]/30 blur-3xl -z-10" />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-y border-[#7AB2B2]" data-testid="stats-section">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 gap-6">
              <Counter target={stats.photos} label="منتج" />
              <Counter target={stats.reservations} label="طلب" />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 md:py-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#333333] mb-4">في إمداد تجد كل ما تحتاج</h2>
            <p className="text-[#666666] text-lg">ثماني مجموعات من الأدوات تعمل معاً كفريق متكامل حول تاجر واحد: أنت.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURE_GROUPS.map((f) => <FeatureCard key={f.testid} {...f} />)}
          </div>
        </div>
      </section>

      {/* Pattern strip */}
      <section className="relative py-16 bg-[#09637E] overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img src={IMG_SHOWROOM} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 text-center text-white">
          <p className="text-2xl md:text-3xl font-extrabold leading-relaxed mb-2" data-testid="quote-strip-text">
            "بِع، ورِّد، حاسِب، وسوِّق — وأنشئ فيديوهاتك، كل ذلك في مكان واحد: إمداد."
          </p>
          <p className="opacity-80" data-testid="quote-strip-subline">إمداد — المنصة الأولى في سوريا المتكاملة لخدمة التاجر</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center bg-white border border-[#7AB2B2] rounded-3xl p-10 md:p-16 shadow-sm">
          <h2 className="text-3xl md:text-5xl font-extrabold text-[#333333] mb-4">ابدأ إدارة عملك من مكان واحد</h2>
          <p className="text-lg md:text-xl text-[#666666] mb-8">انضم إلى تجار إمداد واجعل المنصة فريقك في البيع والتوريد والمحاسبة والتسويق</p>
          <Link to={ctaTo} data-testid="cta-button"
            className="inline-flex items-center gap-2 h-14 px-10 rounded-xl bg-[#09637E] hover:bg-[#088395] text-white font-extrabold text-lg shadow-lg shadow-[#09637E]/20 transition-all hover:-translate-y-0.5">
            {ctaLabel} <ArrowLeft size={20} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#09637E] text-white py-10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div>
                <div className="font-extrabold text-lg">إمداد</div>
                <div className="text-xs opacity-80">منصة التاجر المتكاملة في سوريا</div>
              </div>
            </div>
            <nav className="flex items-center gap-1 sm:gap-3 text-sm flex-wrap justify-center">
              <Link to="/about" className="inline-flex items-center min-h-[44px] px-2 hover:underline opacity-90 hover:opacity-100" data-testid="footer-about">عن المنصة</Link>
              <span className="opacity-30">|</span>
              <Link to="/contact" className="inline-flex items-center min-h-[44px] px-2 hover:underline opacity-90 hover:opacity-100" data-testid="footer-contact">اتصل بنا</Link>
              <span className="opacity-30">|</span>
              <Link to="/terms" className="inline-flex items-center min-h-[44px] px-2 hover:underline opacity-90 hover:opacity-100" data-testid="footer-terms">الشروط والأحكام</Link>
            </nav>
            <div className="text-sm opacity-80" data-testid="footer-copyright">
              © 2026 إمداد - جميع الحقوق محفوظة
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
